import { httpClient } from './httpClient';
import { buildQueryParams } from './queryParams';
import { UserData } from '../hooks/useUser';

// Define interfaces for all your entities
interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName?: string;
      isEmailVerified: boolean;
    };
    token: string;
    refreshToken: string;
  };
  message: string;
}

interface TokenResponse {
  data: {
    token: string;
    refreshToken: string;
  };
}

interface UserResponse {
  success: boolean;
  data: UserData;
}

interface Entity {
  id: string;
  name?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items: T[];
    total: number;
    page: number;
    limit: number;
  };
}

interface EntityResponse {
  success: boolean;
  data: Entity;
}

interface OnboardingData {
  id: string;
  email: string;
  formData: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface OnboardingResponse {
  success: boolean;
  data: OnboardingData;
  message: string;
}

class ApiService {
  private client = httpClient;

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/login', credentials);
    return response.data;
  }

  async register(data: { email: string; password: string; firstName: string }): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/register', data);
    return response.data;
  }

  async refresh(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    const response = await this.client.post<TokenResponse>('/refresh', { refreshToken });
    return response.data.data;
  }

  async getMe(): Promise<UserResponse> {
    const response = await this.client.get<UserResponse>('/me');
    return response.data;
  }

  // Standard CRUD pattern for entities
  async getEntities(page = 1, limit = 50, search?: string): Promise<PaginatedResponse<Entity>> {
    const params = buildQueryParams({ page, limit, search });
    const response = await this.client.get<PaginatedResponse<Entity>>('/entities', { params });
    return response.data;
  }

  async getEntity(id: string): Promise<EntityResponse> {
    const response = await this.client.get<EntityResponse>(`/entities/${id}`);
    return response.data;
  }

  async createEntity(data: Omit<Entity, 'id' | 'createdAt' | 'updatedAt'>): Promise<EntityResponse> {
    const response = await this.client.post<EntityResponse>('/entities', data);
    return response.data;
  }

  async updateEntity(id: string, data: Partial<Omit<Entity, 'id' | 'createdAt' | 'updatedAt'>>): Promise<EntityResponse> {
    const response = await this.client.put<EntityResponse>(`/entities/${id}`, data);
    return response.data;
  }

  async deleteEntity(id: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete<{ success: boolean; message: string }>(`/entities/${id}`);
    return response.data;
  }

  // Onboarding (public endpoints - no auth required)
  async createOnboarding(data: { email: string; formData?: Record<string, unknown> }): Promise<OnboardingResponse> {
    const response = await this.client.post<OnboardingResponse>('/onboardings', data);
    return response.data;
  }

  async updateOnboarding(
    id: string,
    data: { email?: string; formData?: Record<string, unknown> }
  ): Promise<OnboardingResponse> {
    const response = await this.client.patch<OnboardingResponse>(`/onboardings/${id}`, data);
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
